/**
 * DeepLinkListener (T27.2.11) — routes an inbound Android App Link into the SPA.
 *
 * When the installed app is opened by a tapped `https://<appLinksHost>/…` link
 * (from email, a browser, a message), Android hands the app the URL via
 * `@capacitor/app`'s `appUrlOpen` event. This component subscribes to that event
 * and `useNavigate()`s to the link's `pathname + search + hash`, so the tap lands
 * on the matching in-app screen instead of re-loading the WebView at its root.
 *
 * Native-only, mirroring the other native listeners wired in main.tsx:
 *   - the whole effect is gated behind `isNativePlatform()`, so on web it does
 *     nothing and never touches Capacitor;
 *   - `@capacitor/app` is reached only through a dynamic `import()`, so the web
 *     bundle never eagerly loads the native plugin (CP1) and `packages/shared`
 *     stays platform-free (CP1–CP4) — the plugin import lives here in apps/web.
 *
 * Host guard. Only a URL whose host matches the app's own origin is routed;
 * anything else (a foreign https link the OS happened to route here, a malformed
 * URL) is ignored rather than navigated — defence against misrouting a path that
 * isn't ours. The `market://` update deep-link (T27.2.9) is a different scheme
 * and never reaches this listener.
 *
 * Renders nothing (a listener, not UI). Mount once near the app root, beside
 * <AppUpdatePrompt>.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isNativePlatform } from '../routes/nativeGoogleSignIn';

/**
 * The set of hosts this app answers App Links for. The prod + dev web origins;
 * the per-flavor Android manifest only ever hands us one of them (its own
 * `appLinksHost`), but accepting both keeps the listener flavor-agnostic and
 * matches the all-variant `assetlinks.json`. A URL on any other host is ignored.
 */
const APP_LINK_HOSTS = new Set(['app.shopista.ca', 'dev.shopista.ca']);

/**
 * Map an opened URL to the in-app route to navigate to, or null when the URL is
 * not one of ours (foreign host, unparseable). Exported for unit tests — pure,
 * no navigation, no Capacitor.
 */
export function resolveDeepLinkTarget(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Malformed / non-URL payload — ignore rather than misroute.
    return null;
  }
  if (!APP_LINK_HOSTS.has(parsed.host)) return null;
  // pathname + search + hash — the full in-app location, minus origin. Empty
  // pathname is normalized to "/" by the URL parser, so a bare-host link lands
  // on the app root.
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * Mount once near the app root. Native-only: subscribes to `appUrlOpen` and
 * navigates the SPA to the opened App Link's path. Inert on web.
 */
export function DeepLinkListener(): null {
  const navigate = useNavigate();

  useEffect(() => {
    // Web: do nothing, never load the plugin.
    if (!isNativePlatform()) return;

    let removeListener: (() => void) | undefined;
    let cancelled = false;

    // Dynamic import keeps @capacitor/app out of the eager web bundle; it is
    // only pulled in on a native platform. Mirrors the App-lifecycle wiring in
    // main.tsx.
    void import('@capacitor/app').then(({ App }) => {
      void App.addListener('appUrlOpen', ({ url }) => {
        const target = resolveDeepLinkTarget(url);
        if (target) navigate(target);
      }).then((handle) => {
        // The effect may have already cleaned up before the async listener
        // registered — if so, remove it immediately.
        if (cancelled) {
          void handle.remove();
        } else {
          removeListener = () => {
            void handle.remove();
          };
        }
      });
    });

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, [navigate]);

  return null;
}
